/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { defaultAttribute, SimpleAttributeList } from '@touchplan/common';
import { DefaultSplitDataRenderType, SplitData } from '@touchplan/common';
import { CustomFieldType } from '../state/CustomFieldsApi';

/*
sample:
	{
		stringValue: "Custom Field"
	}
*/
export interface ICustomFieldTicketValue {
	value: string | number; // strictly local
	stringValue?: string;
	numericValue?: number;
	idValue?: string;
	dataType?: string; // local reverse lookup of custom field definition dataType
	type?: string; // local reverse lookup of custom field definition type
}

// internal interface used by CustomFieldsApi only
export interface ICustomFieldTicketValueLocalPayload extends Omit<ICustomFieldTicketValue, 'value'> {
	value?: string | number;
}

/*
sample:
	-MKotoNe3SYMx-715ElO: { <-- custom field id
		numericValue: 12345
	}
*/
export interface ICustomFieldTicket {
	[key: string]: ICustomFieldTicketValueLocalPayload;
}

/*
sample:
	-MRLbISr0MDdmw7sipOR: { <-- ticket id
		-MKotoNe3SYMx-713ElO: { <-- custom field id
			stringValue: "Custom Field"
		},
		-MKotoNe3SYMx-715ElO: { <-- custom field id
			numericValue: 12345
		}
	},
	-MRLYk_phlf_hSCBr7iL: { <-- ticket id
		-MKotoNe3SYMx-715ElO: { <-- custom field id
			numericValue: 12345
		}
	}
*/
export interface ICustomFieldTicketList {
	[key: string]: ICustomFieldTicket;
}

export class CustomFieldTicketValue extends SplitData<ICustomFieldTicket, DefaultSplitDataRenderType> {
	protected useInPlace = true;

	protected attributeList: SimpleAttributeList = {
		'*': defaultAttribute,
	};

	constructor(id: string) {
		super(id, DefaultSplitDataRenderType.database);
	}

	public databaseUpdate(data: object): void {
		super.databaseUpdate(data);

		let val;
		for (const prop in data) {
			if (prop) {
				val = data[prop];

				// This code has this "isNumeric" to deal with the fact that JavaScript treats 0 as a falsy value
				// and every other actual number, like "5" or "3.1415", as a truthy number.
				// Please DO NOT try to simplify, NOT without then regression testing a numeric
				// custom field, successfully storing and reloading a value of 0.
				const isNumeric: boolean = val?.numericValue || val?.numericValue === 0;

				if (this.database[prop] && (val?.stringValue || isNumeric || val?.idValue)) {
					if (isNumeric) {
						this.database[prop].value = val.numericValue;
					} else {
						this.database[prop].value = val.stringValue || val.idValue;
					}
					this.database[prop].dataType = isNumeric ? 'number' : 'string';
				}
			}
		}
	}

	public generateDatabaseOutput(renderType?: DefaultSplitDataRenderType): object {
		const view: ICustomFieldTicket = super.generateDatabaseOutput(renderType) as ICustomFieldTicket;

		for (const customField of Object.values(view)) {
			if (customField.value != null) {
				switch (customField.type) {
					case CustomFieldType.Text:
						customField.stringValue = String(customField.value);
						break;
					case CustomFieldType.Number:
						customField.numericValue = parseFloat(customField.value as string);
						break;
					case CustomFieldType.DropDownList:
						customField.idValue = String(customField.value);
						break;
					case CustomFieldType.Link:
						customField.stringValue = String(customField.value);
				}

				delete customField.value;
				delete customField.dataType;
				delete customField.type;
			}
		}
		return view;
	}

	// this is needed for partially revert ticket value data
	// CustomFieldsApi.revertTicketValues() for more info
	public getDatabaseFields(): ICustomFieldTicket {
		return this.database;
	}
}
